import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '../components/ui/drawer';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import {
  ArrowLeft,
  Activity,
  Wifi,
  WifiOff,
  RotateCcw,
  Flag,
  Users,
  Radio,
  Zap,
  Target,
  ShieldCheck,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { API, getWebSocketBaseUrl, shouldUseWebSockets } from '../lib/api';
import { cn } from '../lib/utils';

const ballDisplay = (ball) => {
  if (ball.is_wicket) return 'W';
  if (ball.is_wide) return ball.runs > 0 ? `Wd${ball.runs}` : 'Wd';
  if (ball.is_no_ball) return ball.runs > 0 ? `Nb${ball.runs}` : 'Nb';
  return `${ball.runs}`;
};

const ballTone = (ball) => {
  if (ball.is_wicket) return 'bg-red-500/15 text-red-200 border-red-500/30';
  if (ball.is_wide) return 'bg-amber-500/15 text-amber-200 border-amber-500/30';
  if (ball.is_no_ball) return 'bg-orange-500/15 text-orange-200 border-orange-500/30';
  if (ball.runs >= 4) return 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30';
  if (ball.runs === 0) return 'bg-white/5 text-white/55 border-white/10';
  return 'bg-primary/10 text-primary border-primary/20';
};

const scoringButtons = [
  { label: '0', runs: 0, className: 'border-white/10 bg-white/[0.04] text-white hover:bg-white/[0.09]' },
  { label: '1', runs: 1, className: 'border-white/10 bg-white/[0.04] text-white hover:bg-white/[0.09]' },
  { label: '2', runs: 2, className: 'border-white/10 bg-white/[0.04] text-white hover:bg-white/[0.09]' },
  { label: '3', runs: 3, className: 'border-white/10 bg-white/[0.04] text-white hover:bg-white/[0.09]' },
  { label: '4', runs: 4, className: 'bg-sky-600 text-white hover:bg-sky-500' },
  { label: '5', runs: 5, className: 'border-white/10 bg-white/[0.04] text-white hover:bg-white/[0.09]' },
  { label: '6', runs: 6, className: 'bg-violet-600 text-white hover:bg-violet-500' },
];

const quickActions = [
  {
    key: 'wide',
    label: 'Wide',
    className: 'border-amber-500/25 bg-amber-500/12 text-amber-200 hover:bg-amber-500/20',
    extras: { is_wide: true },
    runs: 0,
  },
  {
    key: 'noball',
    label: 'No Ball',
    className: 'border-orange-500/25 bg-orange-500/12 text-orange-200 hover:bg-orange-500/20',
    extras: { is_no_ball: true },
    runs: 1,
  },
  {
    key: 'bye',
    label: 'Bye',
    className: 'border-white/10 bg-white/[0.05] text-white hover:bg-white/[0.09]',
    extras: { is_bye: true },
    runs: 1,
  },
  {
    key: 'wicket',
    label: 'Wicket',
    className: 'bg-red-600 text-white hover:bg-red-500',
    extras: { is_wicket: true, wicket_type: 'bowled' },
    runs: 0,
  },
];

const PlayerSelector = ({ label, value, onValueChange, options, placeholder, testId }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.24em] text-white/45">{label}</Label>
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className="h-12 rounded-2xl border-white/10 bg-black/20 text-white" data-testid={testId}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((player) => (
          <SelectItem key={player.id} value={player.id}>
            {player.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const MobileStat = ({ label, value, highlight = false }) => (
  <div className="rounded-[18px] border border-white/10 bg-black/20 p-2.5 text-center sm:rounded-2xl sm:p-3">
    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/35 sm:text-[11px] sm:tracking-[0.24em]">{label}</p>
    <p className={cn('mt-1.5 text-lg font-bold score-display sm:mt-2 sm:text-xl', highlight ? 'text-primary' : 'text-white')}>
      {value}
    </p>
  </div>
);

const LiveScoring = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [match, setMatch] = useState(null);
  const [teams, setTeams] = useState({ batting: null, bowling: null });
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [tossDialogOpen, setTossDialogOpen] = useState(false);
  const [selectPlayersOpen, setSelectPlayersOpen] = useState(false);
  const [controlDrawerOpen, setControlDrawerOpen] = useState(false);

  const [tossData, setTossData] = useState({ won_by: '', elected_to: '' });
  const [selectedBatsman, setSelectedBatsman] = useState('');
  const [selectedNonStriker, setSelectedNonStriker] = useState('');
  const [selectedBowler, setSelectedBowler] = useState('');

  const wsRef = useRef(null);
  const websocketEnabled = shouldUseWebSockets();

  const getPlayerName = useCallback(
    (playerId, squadKey) => {
      if (!playerId) return 'Not selected';
      const players = teams[squadKey]?.players || [];
      return players.find((player) => player.id === playerId)?.name || 'Unknown';
    },
    [teams]
  );

  const fetchTeams = useCallback(async (matchData) => {
    try {
      const currentInnings = matchData.innings[matchData.innings.length - 1];
      const [battingTeam, bowlingTeam] = await Promise.all([
        axios.get(`${API}/teams/${currentInnings.batting_team.id}`, getAuthHeader()),
        axios.get(`${API}/teams/${currentInnings.bowling_team.id}`, getAuthHeader()),
      ]);
      setTeams({ batting: battingTeam.data, bowling: bowlingTeam.data });
    } catch (error) {
      console.error('Failed to fetch teams');
    }
  }, [getAuthHeader]);

  const connectWebSocket = useCallback(() => {
    if (!websocketEnabled) return;

    const wsUrl = getWebSocketBaseUrl();
    if (!wsUrl) return;

    const ws = new WebSocket(`${wsUrl}/ws/match/${matchId}`);

    ws.onopen = () => setWsConnected(true);
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'initial_state' || data.type === 'ball_update' || data.type === 'innings_end') {
        setMatch(data.match);
      }
    };
    ws.onclose = () => {
      setWsConnected(false);
      setTimeout(connectWebSocket, 3000);
    };
    ws.onerror = () => setWsConnected(false);

    wsRef.current = ws;
  }, [matchId, websocketEnabled]);

  const fetchMatch = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/matches/${matchId}/summary`, getAuthHeader());
      setMatch(response.data);

      if (response.data.status === 'scheduled') {
        setTossDialogOpen(true);
      } else if (response.data.innings?.length > 0) {
        await fetchTeams(response.data);
      }
    } catch (error) {
      toast.error('Failed to load match');
    } finally {
      setLoading(false);
    }
  }, [matchId, getAuthHeader, fetchTeams]);

  useEffect(() => {
    fetchMatch();
    if (websocketEnabled) connectWebSocket();

    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, [fetchMatch, connectWebSocket, websocketEnabled]);

  useEffect(() => {
    const refreshInterval = setInterval(fetchMatch, websocketEnabled ? 10000 : 3000);
    return () => clearInterval(refreshInterval);
  }, [fetchMatch, websocketEnabled]);

  const handleToss = async () => {
    if (!tossData.won_by || !tossData.elected_to) {
      toast.error('Please complete toss details');
      return;
    }

    try {
      const response = await axios.post(`${API}/matches/${matchId}/toss`, tossData, getAuthHeader());
      setMatch(response.data);
      setTossDialogOpen(false);
      setSelectPlayersOpen(true);
      await fetchTeams(response.data);
      toast.success('Toss recorded! Select opening players');
    } catch (error) {
      toast.error('Failed to record toss');
    }
  };

  const handleBallInput = async (runs, extras = {}) => {
    if (!selectedBatsman || !selectedBowler) {
      toast.error('Please select batsman and bowler');
      setControlDrawerOpen(true);
      return;
    }

    try {
      const ballData = {
        runs,
        is_wicket: extras.is_wicket || false,
        wicket_type: extras.wicket_type || null,
        is_wide: extras.is_wide || false,
        is_no_ball: extras.is_no_ball || false,
        is_bye: extras.is_bye || false,
        is_leg_bye: extras.is_leg_bye || false,
        bowler_id: selectedBowler,
        batsman_id: selectedBatsman,
        non_striker_id: selectedNonStriker || selectedBatsman,
      };

      const response = await axios.post(`${API}/matches/${matchId}/ball`, ballData, getAuthHeader());
      setMatch(response.data);

      if (runs % 2 === 1 && !extras.is_wicket) {
        const temp = selectedBatsman;
        setSelectedBatsman(selectedNonStriker);
        setSelectedNonStriker(temp);
      }

      const innings = response.data.innings[response.data.innings.length - 1];
      if (innings.balls === 0 && innings.overs > 0) {
        const temp = selectedBatsman;
        setSelectedBatsman(selectedNonStriker);
        setSelectedNonStriker(temp);
        toast.info('End of over');
      }

      if (extras.is_wicket) {
        setSelectedBatsman('');
        setControlDrawerOpen(true);
        toast.info('Select new batsman');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record ball');
    }
  };

  const handleEndInnings = async () => {
    if (!window.confirm('End current innings?')) return;

    try {
      const response = await axios.post(`${API}/matches/${matchId}/end-innings`, {}, getAuthHeader());
      setMatch(response.data);

      if (response.data.status === 'completed') {
        toast.success('Match completed!');
        navigate(`/live/${matchId}`);
      } else {
        await fetchTeams(response.data);
        setSelectedBatsman('');
        setSelectedNonStriker('');
        setSelectedBowler('');
        setSelectPlayersOpen(true);
        setControlDrawerOpen(true);
        toast.success('Innings ended. Select opening players for 2nd innings');
      }
    } catch (error) {
      toast.error('Failed to end innings');
    }
  };

  const rotateStrike = () => {
    if (!selectedBatsman || !selectedNonStriker) return;
    const temp = selectedBatsman;
    setSelectedBatsman(selectedNonStriker);
    setSelectedNonStriker(temp);
  };

  const confirmLineup = () => {
    if (!selectedBatsman || !selectedNonStriker || !selectedBowler) {
      toast.error('Complete striker, non-striker, and bowler selection');
      return;
    }
    setSelectPlayersOpen(false);
    setControlDrawerOpen(false);
    toast.success('Lineup ready for scoring');
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-primary" />
      </div>
    );
  }

  if (!match) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-muted-foreground">Match not found</p>
      </div>
    );
  }

  const currentInnings = match.innings?.[match.innings.length - 1];
  const runRate = currentInnings && currentInnings.overs > 0
    ? (currentInnings.runs / (currentInnings.overs + currentInnings.balls / 6)).toFixed(2)
    : '0.00';
  const reqRunRate = currentInnings?.target && currentInnings.overs < match.overs
    ? ((currentInnings.target - currentInnings.runs) / (match.overs - currentInnings.overs - currentInnings.balls / 6)).toFixed(2)
    : null;
  const extrasTotal = currentInnings
    ? currentInnings.extras.wides + currentInnings.extras.no_balls + currentInnings.extras.byes + currentInnings.extras.leg_byes
    : 0;

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(170,255,0,0.10),transparent_20%),linear-gradient(180deg,_rgba(10,12,18,1)_0%,_rgba(12,16,23,1)_100%)] text-white">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0b1017]/90 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-3.5 sm:h-16 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <Link to={`/tournaments/${match.tournament_id}/matches`}>
              <Button variant="ghost" size="icon" className="h-9 w-9 rounded-2xl text-white hover:bg-white/[0.08] sm:h-10 sm:w-10">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div className="min-w-0">
              <p className="truncate text-[13px] font-semibold sm:text-sm">{match.team1?.name} vs {match.team2?.name}</p>
              <p className="truncate text-xs text-white/45">{match.round_name}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {websocketEnabled ? (
              wsConnected ? (
                <Badge variant="outline" className="rounded-full border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
                  <Wifi className="mr-1 h-3 w-3" /> Live
                </Badge>
              ) : (
                <Badge variant="outline" className="rounded-full border-red-500/30 bg-red-500/10 text-red-300">
                  <WifiOff className="mr-1 h-3 w-3" /> Offline
                </Badge>
              )
            ) : (
              <Badge variant="outline" className="rounded-full border-primary/30 bg-primary/10 text-primary">
                <Activity className="mr-1 h-3 w-3" /> Auto refresh
              </Badge>
            )}
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-3.5 pb-36 pt-3.5 sm:px-6 sm:pb-40 sm:pt-5 lg:pb-10">
        {currentInnings && (
          <div className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
            <Card className="overflow-hidden rounded-[22px] border-primary/20 bg-[linear-gradient(135deg,rgba(170,255,0,0.18),rgba(255,255,255,0.03)_52%,rgba(255,255,255,0.01))] sm:rounded-[28px]">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-2.5 py-1 text-[11px] font-medium text-white/65 sm:px-3 sm:text-xs">
                      <Radio className="h-3.5 w-3.5 text-red-300 pulse-live" />
                      Rapid Match Updater
                    </div>
                    <p className="mt-3 text-[13px] font-medium text-white/70 sm:mt-4 sm:text-sm">{currentInnings.batting_team?.name}</p>
                    <p className="mt-1.5 text-[46px] font-bold leading-none tracking-tight text-white score-display sm:mt-2 sm:text-7xl">
                      {currentInnings.runs}
                      <span className="text-2xl text-white/45 sm:text-4xl">/{currentInnings.wickets}</span>
                    </p>
                    <p className="mt-1 text-base text-white/60 sm:mt-2 sm:text-lg">
                      ({currentInnings.overs}.{currentInnings.balls}) overs
                    </p>
                    {currentInnings.target ? (
                      <div className="mt-3 rounded-[18px] border border-white/10 bg-black/20 px-3 py-2.5 text-[13px] leading-5 text-white/75 sm:hidden">
                        Need <span className="font-semibold text-primary">{Math.max(currentInnings.target - currentInnings.runs, 0)}</span> runs from{' '}
                        <span className="font-semibold text-primary">{Math.max((match.overs * 6) - (currentInnings.overs * 6 + currentInnings.balls), 0)}</span> balls
                      </div>
                    ) : null}
                  </div>

                  <div className="grid w-full grid-cols-2 gap-2 sm:max-w-[260px] sm:gap-3">
                    <MobileStat label="Run Rate" value={runRate} highlight />
                    {currentInnings.target ? (
                      <>
                        <MobileStat label="Target" value={currentInnings.target} />
                        <MobileStat label="Need" value={Math.max(currentInnings.target - currentInnings.runs, 0)} />
                      </>
                    ) : (
                      <>
                        <MobileStat label="Extras" value={extrasTotal} />
                        <MobileStat label="Wickets" value={currentInnings.wickets} />
                      </>
                    )}
                    {reqRunRate ? (
                      <div className="col-span-2">
                        <MobileStat label="Req RR" value={reqRunRate} />
                      </div>
                    ) : null}
                  </div>
                </div>

                {currentInnings.current_over?.length > 0 && (
                  <div className="mt-4 border-t border-white/10 pt-4 sm:mt-5 sm:pt-5">
                    <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/35 sm:mb-3 sm:text-[11px] sm:tracking-[0.28em]">This Over</p>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      {currentInnings.current_over.map((ball, idx) => (
                        <div
                          key={`${ball.id || idx}`}
                          className={`flex h-9 min-w-9 items-center justify-center rounded-full border px-2 text-xs font-semibold sm:h-11 sm:min-w-11 sm:text-sm ${ballTone(ball)}`}
                        >
                          {ballDisplay(ball)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="hidden rounded-[22px] border-white/10 bg-white/[0.04] lg:block sm:rounded-[28px]">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg text-white">
                  <Users className="h-5 w-5 text-primary" />
                  Match Control
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-2xl border border-white/10 bg-black/20 p-3.5 sm:p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/35 sm:text-[11px] sm:tracking-[0.28em]">Striker</p>
                  <p className="mt-1.5 text-[15px] font-semibold text-white sm:mt-2 sm:text-base">{getPlayerName(selectedBatsman, 'batting')}</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-black/20 p-3.5 sm:p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/35 sm:text-[11px] sm:tracking-[0.28em]">Non-Striker</p>
                  <p className="mt-1.5 text-[15px] font-semibold text-white sm:mt-2 sm:text-base">{getPlayerName(selectedNonStriker, 'batting')}</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-black/20 p-3.5 sm:p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/35 sm:text-[11px] sm:tracking-[0.28em]">Bowler</p>
                  <p className="mt-1.5 text-[15px] font-semibold text-white sm:mt-2 sm:text-base">{getPlayerName(selectedBowler, 'bowling')}</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Button
                    variant="outline"
                    className="h-12 rounded-2xl border-white/10 bg-white/[0.04] text-white hover:bg-white/[0.08]"
                    onClick={() => setControlDrawerOpen(true)}
                  >
                    <Target className="h-4 w-4" />
                    Manage Lineup
                  </Button>
                  <Button
                    variant="outline"
                    className="h-12 rounded-2xl border-white/10 bg-white/[0.04] text-white hover:bg-white/[0.08]"
                    onClick={rotateStrike}
                    disabled={!selectedBatsman || !selectedNonStriker}
                    data-testid="rotate-strike-button"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Rotate Strike
                  </Button>
                </div>
                <Button
                  variant="outline"
                  className="h-12 w-full rounded-2xl border-red-500/20 bg-red-500/10 text-red-200 hover:bg-red-500/16"
                  onClick={handleEndInnings}
                  data-testid="end-innings-button"
                >
                  <Flag className="h-4 w-4" />
                  End Innings
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {match.status === 'live' && (
          <div className="mt-4 grid gap-4 lg:mt-5 lg:grid-cols-[1.15fr_0.85fr] lg:gap-5">
            <Card className="rounded-[22px] border-white/10 bg-white/[0.04] sm:rounded-[28px]">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg text-white">
                  <Zap className="h-5 w-5 text-primary" />
                  Scoring Surface
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-4 gap-2 sm:grid-cols-7 sm:gap-3">
                  {scoringButtons.map((button) => (
                    <Button
                      key={button.label}
                      className={`h-14 rounded-2xl text-xl font-bold touch-target sm:h-16 sm:text-2xl ${button.className}`}
                      onClick={() => handleBallInput(button.runs)}
                      data-testid={`score-button-${button.runs}`}
                    >
                      {button.label}
                    </Button>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
                  {quickActions.map((action) => (
                    <Button
                      key={action.key}
                      variant="outline"
                      className={`h-12 rounded-2xl text-[13px] font-semibold sm:h-14 sm:text-sm ${action.className}`}
                      onClick={() => handleBallInput(action.runs, action.extras)}
                      data-testid={`score-button-${action.key}`}
                    >
                      {action.label}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="hidden rounded-[22px] border-white/10 bg-white/[0.04] lg:block sm:rounded-[28px]">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg text-white">
                  <ShieldCheck className="h-5 w-5 text-primary" />
                  Manager Flow
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-white/65">
                <div className="rounded-2xl border border-white/10 bg-black/20 p-3.5 sm:p-4">
                  <p className="font-semibold text-white">One-handed workflow</p>
                  <p className="mt-2 leading-6">
                    The bottom action model keeps scoring fast on mobile. Use the control sheet for player changes and innings actions without leaving the match.
                  </p>
                </div>
                <button
                  onClick={() => setControlDrawerOpen(true)}
                  className="flex w-full items-center justify-between rounded-2xl border border-white/10 bg-primary/10 p-4 text-left transition-colors hover:bg-primary/15"
                >
                  <div>
                    <p className="font-semibold text-white">Open Rapid Controls</p>
                    <p className="mt-1 text-sm text-white/55">Lineup, strike rotation, and innings controls</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-primary" />
                </button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {match.status === 'live' && (
        <>
          <div className="fixed inset-x-0 bottom-0 z-30 border-t border-white/10 bg-[#0b1017]/92 px-3.5 pb-[max(env(safe-area-inset-bottom),0.85rem)] pt-2.5 backdrop-blur-xl lg:hidden">
            <div className="mx-auto max-w-lg">
              <div className="mb-2.5 flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.04] px-3.5 py-2.5">
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/35">Ready to Score</p>
                  <p className="truncate text-[13px] font-medium text-white">
                    {selectedBatsman && selectedBowler
                      ? `${getPlayerName(selectedBatsman, 'batting')} vs ${getPlayerName(selectedBowler, 'bowling')}`
                      : 'Select striker and bowler'}
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="h-9 rounded-full border-white/10 bg-white/[0.05] px-3 text-[13px] text-white hover:bg-white/[0.08]"
                  onClick={() => setControlDrawerOpen(true)}
                >
                  Controls
                </Button>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[0, 1, 4, 6].map((runs) => (
                  <Button
                    key={`mobile-${runs}`}
                    className={cn(
                      'h-12 rounded-2xl text-lg font-bold',
                      runs === 4 && 'bg-sky-600 text-white hover:bg-sky-500',
                      runs === 6 && 'bg-violet-600 text-white hover:bg-violet-500',
                      runs !== 4 && runs !== 6 && 'border-white/10 bg-white/[0.04] text-white hover:bg-white/[0.09]'
                    )}
                    onClick={() => handleBallInput(runs)}
                  >
                    {runs}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <Drawer open={controlDrawerOpen} onOpenChange={setControlDrawerOpen}>
            <DrawerContent className="max-h-[88vh] rounded-t-[28px] border-white/10 bg-[#0b1017] text-white">
              <DrawerHeader className="px-5 pt-5 text-left">
                <DrawerTitle className="text-xl text-white">Rapid Match Controls</DrawerTitle>
                <DrawerDescription className="text-white/55">
                  Manage current players, control innings flow, and keep match updates moving quickly.
                </DrawerDescription>
              </DrawerHeader>
              <div className="space-y-5 overflow-y-auto px-5 pb-6">
                <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-white/35">Current Assignment</p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <div className="rounded-2xl border border-white/10 bg-black/20 p-3">
                      <p className="text-xs text-white/40">Striker</p>
                      <p className="mt-1 font-semibold text-white">{getPlayerName(selectedBatsman, 'batting')}</p>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-black/20 p-3">
                      <p className="text-xs text-white/40">Non-Striker</p>
                      <p className="mt-1 font-semibold text-white">{getPlayerName(selectedNonStriker, 'batting')}</p>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-black/20 p-3">
                      <p className="text-xs text-white/40">Bowler</p>
                      <p className="mt-1 font-semibold text-white">{getPlayerName(selectedBowler, 'bowling')}</p>
                    </div>
                  </div>
                </div>

                <div className="grid gap-4">
                  <PlayerSelector
                    label="Striker"
                    value={selectedBatsman}
                    onValueChange={setSelectedBatsman}
                    options={teams.batting?.players || []}
                    placeholder="Select striker"
                    testId="striker-select"
                  />
                  <PlayerSelector
                    label="Non-Striker"
                    value={selectedNonStriker}
                    onValueChange={setSelectedNonStriker}
                    options={(teams.batting?.players || []).filter((player) => player.id !== selectedBatsman)}
                    placeholder="Select non-striker"
                    testId="non-striker-select"
                  />
                  <PlayerSelector
                    label="Bowler"
                    value={selectedBowler}
                    onValueChange={setSelectedBowler}
                    options={teams.bowling?.players || []}
                    placeholder="Select bowler"
                    testId="bowler-select"
                  />
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <Button
                    variant="outline"
                    className="h-12 rounded-2xl border-white/10 bg-white/[0.04] text-white hover:bg-white/[0.08]"
                    onClick={rotateStrike}
                    disabled={!selectedBatsman || !selectedNonStriker}
                    data-testid="rotate-strike-button-drawer"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Rotate Strike
                  </Button>
                  <Button
                    variant="outline"
                    className="h-12 rounded-2xl border-red-500/20 bg-red-500/10 text-red-200 hover:bg-red-500/16"
                    onClick={handleEndInnings}
                    data-testid="end-innings-button-drawer"
                  >
                    <Flag className="h-4 w-4" />
                    End Innings
                  </Button>
                </div>
              </div>
              <DrawerFooter className="border-t border-white/10 bg-[#0b1017] px-5 pb-5 pt-4">
                <Button
                  onClick={confirmLineup}
                  className="h-12 rounded-2xl"
                  disabled={!selectedBatsman || !selectedNonStriker || !selectedBowler}
                  data-testid="confirm-players-button"
                >
                  Save Match Controls
                </Button>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>
        </>
      )}

      <Dialog open={tossDialogOpen} onOpenChange={setTossDialogOpen}>
        <DialogContent className="border-white/10 bg-[#0b1017] text-white">
          <DialogHeader>
            <DialogTitle>Toss Setup</DialogTitle>
            <DialogDescription className="text-white/55">
              Record the toss result to start the match.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <PlayerSelector
              label="Toss Won By"
              value={tossData.won_by}
              onValueChange={(value) => setTossData({ ...tossData, won_by: value })}
              options={[
                { id: 'team1', name: match.team1?.name || 'Team 1' },
                { id: 'team2', name: match.team2?.name || 'Team 2' },
              ]}
              placeholder="Select team"
              testId="toss-winner-select"
            />
            <PlayerSelector
              label="Elected To"
              value={tossData.elected_to}
              onValueChange={(value) => setTossData({ ...tossData, elected_to: value })}
              options={[
                { id: 'bat', name: 'Bat' },
                { id: 'bowl', name: 'Bowl' },
              ]}
              placeholder="Select decision"
              testId="toss-decision-select"
            />
            <Button onClick={handleToss} className="h-12 w-full rounded-2xl" data-testid="submit-toss-button">
              Start Match
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={selectPlayersOpen} onOpenChange={setSelectPlayersOpen}>
        <DialogContent className="border-white/10 bg-[#0b1017] text-white">
          <DialogHeader>
            <DialogTitle>Select Opening Players</DialogTitle>
            <DialogDescription className="text-white/55">
              Choose the batting pair and bowler to begin this innings.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <PlayerSelector
              label="Opening Batsman (Striker)"
              value={selectedBatsman}
              onValueChange={setSelectedBatsman}
              options={teams.batting?.players || []}
              placeholder="Select batsman"
              testId="opening-batsman-select"
            />
            <PlayerSelector
              label="Opening Batsman (Non-Striker)"
              value={selectedNonStriker}
              onValueChange={setSelectedNonStriker}
              options={(teams.batting?.players || []).filter((player) => player.id !== selectedBatsman)}
              placeholder="Select batsman"
              testId="non-striker-batsman-select"
            />
            <PlayerSelector
              label="Opening Bowler"
              value={selectedBowler}
              onValueChange={setSelectedBowler}
              options={teams.bowling?.players || []}
              placeholder="Select bowler"
              testId="opening-bowler-select"
            />
            <Button
              onClick={confirmLineup}
              className="h-12 w-full rounded-2xl"
              disabled={!selectedBatsman || !selectedNonStriker || !selectedBowler}
              data-testid="confirm-opening-players-button"
            >
              Start Scoring
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LiveScoring;
